import { enqueueSnackbar } from "notistack";
import { getPossibleTicketUsers, postAddTicket } from "../../../../../api/tickets";
import convertTicketFieldValuesToHTML from "./SpecialFieldTypes/ConvertTicketFieldValuesToHTML";
import { ticketMessageSubmitAllPictures } from "./ticket-message-input-cleanup";
import { fileFormat } from "../../../../../reusable-components/file-thumbnail/utils";

export function getPossibleUsers(setPossibleUsers, confirmedCategoryFacilityAndType, ticketFacility, ticketCategory, ticketType) {
  setPossibleUsers(null);
  if (!confirmedCategoryFacilityAndType || !ticketFacility?.id) return;
  getPossibleTicketUsers(ticketFacility.id, ticketCategory?.value ?? 0, ticketType?.value ?? 0)
    .then((res) => setPossibleUsers(res.data ?? []))
    .catch(() => setPossibleUsers([]));
}

export function setInitialTicketHtmlHelper(setOpenTicketMessageEditer, ticketType, ticketTypeFieldGroups, setSubject, setNewUsers, setTicketHtml, user, applyAutoSignature) {
  setOpenTicketMessageEditer(true);
  let defaultSubject = ticketType?.defaultSubject ?? '';
  ticketTypeFieldGroups.flatMap((group) => group.fields)
    .forEach((field) => { defaultSubject = defaultSubject.replace(`{${field.id} - ${field.name}}`, field.subjectValue ?? field.value ?? ''); });
  setSubject(defaultSubject);
  setNewUsers([
    { value: `${user?.firstName} ${user?.lastName}`, id: user?.email },
    ...ticketTypeFieldGroups.flatMap((group) => group.fields.map((field) => field.addUser)).filter((addUser) => addUser && addUser.id !== user?.email),
  ]);
  setTicketHtml(convertTicketFieldValuesToHTML(ticketTypeFieldGroups) + (applyAutoSignature ? getTicketMessageAutoSignature(user) : ""));
};

export function getTicketMessageAutoSignature(user) {
  return user.settings?.find((setting) => setting.show && setting.areaId === 'AutoSignatureOnTickets')?.value ||
      `<br><p>Thank you,</p><p>${user.firstName} ${user.lastName}</p>`;
}

export function getHeaderGroups(ticketTypeFieldGroups) {
  return (ticketTypeFieldGroups ?? []).filter((group) => group.headerGroup).slice(0, -1).map((group) => !group.complete ? 0 : 100);
};

export async function submitTicket(ticketHtml, ticketType, ticketTypeFieldGroups, subject, ticketFacility, ticketCategory, attachments, newUsers, onSuccess,
  setSubmittingTicket, navigate, user, applyAutoSignature) {
  setSubmittingTicket(true);
  const mentionedUsers = Array.from(new DOMParser().parseFromString(ticketHtml, 'text/html').querySelectorAll('span.mention'))
    .map((mention) => mention.getAttribute('data-id'));
  const orderNumber = ticketType?.name?.toLowerCase().includes('order')
    ? ticketTypeFieldGroups.flatMap((group) => group.fields.map((field) => field.orderId)).find((id) => id > 0) ?? 0
    : 0;
  const message = await ticketMessageSubmitAllPictures(ticketHtml?.trim() && applyAutoSignature && user ? `${ticketHtml} ${getTicketMessageAutoSignature(user)}` : ticketHtml);
  const ticketData = {
    subject,
    message,
    facilityId: ticketFacility?.id || null,
    ticketCategoryId: ticketCategory?.value ?? 0,
    ticketTypeId: ticketType?.value ?? ticketType?.id,
    files: attachments,
    users: [...newUsers?.map((t) => t.id), ...mentionedUsers],
    notify: true,
  };
  if(orderNumber) {
    const orderType = ticketTypeFieldGroups.flatMap((group) => group.fields.map((field) => field.orderType)).find((type) => type);
    if(orderType === 'Rental') ticketData.ticketRentalId = orderNumber;
    else if(orderType === 'Purchase') ticketData.ticketOrderId = orderNumber;
  }
  postAddTicket(ticketData).then((res) => {
    if (res.data?.id) {
      if (onSuccess) onSuccess();
      navigate(`/dashboard/tickets/${res.data.id}`);
    } else enqueueSnackbar("Failed to add ticket", { variant: "error" });
  }).catch(() => enqueueSnackbar("Failed to add ticket", { variant: "error" }))
    .finally(() => setSubmittingTicket(false));
};

export function changeActiveGroup(setTicketTypeFieldGroups, setInitialTicketHtml, direction = "next")
{
  setTicketTypeFieldGroups((groups) => {
    if (direction === "next") {
      const updateGroupIndex = groups.findIndex((group) => group.headerGroup && !group.complete);
      if (updateGroupIndex >= 0) groups[updateGroupIndex].complete = true;
    } else if (direction === "back") {
      const lastCompleteIndex = [...groups].reverse().findIndex((group) => group.headerGroup && group.complete);
      if (lastCompleteIndex === -1) return [];   
      const idx = groups.length - 1 - lastCompleteIndex;
      groups[idx].complete = false;
    }

    const showFrom = groups.findIndex((group) => group.headerGroup && !group.complete);
    const showUntil = groups.findIndex((group, index) => index > showFrom && group.headerGroup && !group.complete);

    if (showFrom === -1 && showUntil === -1) {
      setInitialTicketHtml();
      return groups.map((group) => ({ ...group, active: false }));
    }

    return groups.map((group, index) => ({
      ...group,
      active: (showFrom === -1 || index >= showFrom) && (showUntil === -1 || index < showUntil),
    }));
  });
};

export function setTicketTypeFieldGroupsHelper(type, setTicketTypeFieldGroups, confirmedCategoryFacilityAndType, setInitialTicketHtml)
{
  const newFieldGroups = type?.fieldGroups.map(group => ({
    ...group,
    complete: false,
    fields: group.fields.map((field) => ({
      ...field,
      dropdownOptions: field.dropdownOptions?.map(val => typeof val === "string" ? { value: val, label: val } : val),
      value: null,
      htmlValue: null,
      subjectValue: null,
      addUser: null,
      orderId: null,
    })),
  })).filter((group) => group.fields.length > 0) ?? [];
  const showFrom = newFieldGroups.findIndex((group) => group.headerGroup && !group.complete);
  const showUntil = newFieldGroups.findIndex((group, index) => index > showFrom && group.headerGroup);
  const ticketTypeFieldGroups = newFieldGroups.map((group, index) => ({ ...group, active: showUntil === -1 || index < showUntil }));
  setTicketTypeFieldGroups(ticketTypeFieldGroups);
  if (setInitialTicketHtml && confirmedCategoryFacilityAndType && !ticketTypeFieldGroups.some(group => group?.fields.some(field => field.name && field.type && field.type !== "No Input Text")))
    setInitialTicketHtml();
}

export function filterFacCatType(facility, facilities, facilityCategories, facilityId, facilityTypes, isTestTicket, setFilteredCategories, setFilteredTypes, ticketCategoriesFullList,
  ticketTypes, ticketCategory, ticketType, setFacility, setTicketCategory, setSelectedTicketType, setConfirmedCategoryFacilityAndType, userClearedFacility = false) {
  const selectedFacility = facility || (facilities?.length === 1 ? facilities[0] : null) ||
    facilities?.find((facility) => facility.id === facilityId) || (facilities?.length === 0 ? { id: facilityId, label: '' } : null);
  const newFilteredCategories = facilityCategories?.length ? selectedFacility ? ticketCategoriesFullList
    .filter(category => facilityCategories.some(facCat => facCat.key === selectedFacility.id && facCat.value === category.value)) : [] : ticketCategoriesFullList;
  const newFilteredTypes = [...(facilityTypes ? selectedFacility && facilityTypes[selectedFacility.id] ? ticketTypes.filter(type => facilityTypes[selectedFacility.id].includes(type.id)) : [] : ticketTypes)]
    .sort((a, b) => a.name.localeCompare(b.name));
  setFilteredCategories(newFilteredCategories);
  setFilteredTypes(newFilteredTypes);
  const selectedType = isTestTicket ? -1 : (newFilteredTypes?.length === 1 ? newFilteredTypes[0].id : null);
  if (!facility && !userClearedFacility && selectedFacility) setFacility(selectedFacility);
  if (!ticketCategory || !newFilteredCategories.some(cat => cat.value === ticketCategory.value)) setTicketCategory(newFilteredCategories?.length === 1 ? newFilteredCategories[0] : null);
  if (!ticketType || !newFilteredTypes.some(type => type.id === selectedType)) setSelectedTicketType(ticketTypes.find(type => type.id === selectedType) || null);
  if (facilities.length < 2 && ticketCategoriesFullList.length < 2 && ticketTypes.length < 2) setConfirmedCategoryFacilityAndType(true);  
}

export function getFullCategoryList(ticketCategories, facilityCategories, user) {
  const sortedCategories = [...(ticketCategories ?? [])].sort((a, b) => a.label.localeCompare(b.label));
  const shouldAddOther = sortedCategories.length > 0 && (facilityCategories?.find(cat => cat.value === 0) || user.roles.includes('Admin') || user.roles.includes('Super-Admin'));
  return shouldAddOther ? [...sortedCategories, { value: 0, label: 'Other' }] : sortedCategories;
}

export function getFullFacilityList(facilities) {
  return (facilities ?? []).filter(facility => !facility.viewOnly);
}

export function getFullTypeList(ticketTypes, facilityTypes, user) {
  const shouldAddOther = ticketTypes?.length > 0 && ((facilityTypes && Object.entries(facilityTypes).some(x => x[1]?.includes(0)))
    || user.roles.includes('Admin') || user.roles.includes('Super-Admin'));
  return shouldAddOther ? [...ticketTypes, { id: 0, value: 0, label: 'Other', name: 'Other', fieldGroups: [] }] : ticketTypes;
}

export function ticketMessageSubmitErrorMessage(message, attachments, subject, autoSignature) {
  if (!subject?.trim()) return "Subject must be filled";
  if ((!message?.trim() || message === autoSignature || message === "<p><br></p>") && !attachments?.length) return "Message must be filled";
  return "";
}

export function getAllImagesForLighthouse(messages) {
  const IMAGES = ['jpg', 'jpeg', 'gif', 'bmp', 'png', 'svg'];

  return messages?.filter((message) => message.note.includes('<img src="') || (message.file && IMAGES.includes(message.note.split('.').pop())))
    .flatMap((message) => {
      if (message.file) return [`/api/TicketFile?noteId=${message.id}`];
      if (message.note.includes('img src="')) {
        const imageSources = message.note.matchAll(/src="([^"]*)"/g);
        return Array.from(imageSources, (match) => match[1]);
      }
      return [];
    }).filter(img => img).map(img => ({ src: img }));
}

export function getImageID(imageUrl) {
  const localIdRegex = /\/TicketFile\?noteId=(\d+)/;
  const idRegex = /\/(\w+)\.\w+$/;
  if (localIdRegex.test(imageUrl)) return imageUrl.match(localIdRegex)[1];
  if (idRegex.test(imageUrl)) return imageUrl.match(idRegex)[1];
  return imageUrl;
}

// Collects the media, docs and links shared in a ticket's messages, newest first.
// Inline images in email messages are skipped so signature logos don't show up as media.
export function getTicketSharedContent(messages) {
  const media = [];
  const docs = [];
  const links = [];
  const seenLinks = new Set();

  const addLink = (url, text, sender) => {
    const cleanUrl = url.trim().replace(/[.,;:!?)\]]+$/, '');
    if (!/^https?:\/\//i.test(cleanUrl) || /\/TicketFile\?noteId=/i.test(cleanUrl) || seenLinks.has(cleanUrl)) return;
    seenLinks.add(cleanUrl);
    links.push({ ...sender, url: cleanUrl, text: text && text !== cleanUrl ? text : '' });
  };

  [...(messages || [])].reverse().forEach((message) => {
    if (message.systemMessage || !message.note) return;
    const sender = { messageId: message.id, name: message.fullName || message.sentBy, sentOn: message.sentOn };

    if (message.file) {
      const format = fileFormat(message.note);
      if (format === 'image') media.push({ ...sender, src: `/api/TicketFile?noteId=${message.id}` });
      else docs.push({ ...sender, fileName: message.note, isPdf: format === 'pdf' });
      return;
    }

    const html = new DOMParser().parseFromString(message.note, 'text/html');
    if (!message.emailMessage) {
      html.querySelectorAll('img').forEach((img) => {
        const src = img.getAttribute('src');
        if (src) media.push({ ...sender, src });
      });
    }
    html.querySelectorAll('a[href]').forEach((anchor) => {
      const href = anchor.getAttribute('href');
      // some hrefs contain extra text around the url (same case replaceLinks handles)
      const url = href.split(' ').find((part) => /^https?:\/\//i.test(part)) ?? href;
      addLink(url, anchor.textContent.trim(), sender);
      anchor.remove();
    });
    (html.body.textContent.match(/https?:\/\/[^\s<>"']+/gi) || []).forEach((url) => addLink(url, '', sender));
  });

  return { media, docs, links };
}

export function handleAddTicketAttachment(event, attachments, setAttachments)
{
  const maxAllowedFiles = 5;
  if (event.target.files.length > maxAllowedFiles || event.target.files.length + attachments?.length > maxAllowedFiles) {
    alert(`Only ${maxAllowedFiles} files can be uploaded at a time.`);
    event.target.value = '';
    return;
  }
  const { files } = event.target;
  const fileList = [];
  for (let i = 0; i < files.length; i++) {
    if (attachments?.some((x) => x.name === files[i].name)) {
      alert(`File ${files[i].name} already exists.`);
    } else fileList.push(files[i]);
  }
  setAttachments((prevAttachments) => {
    if (prevAttachments === undefined) return fileList;
    return [...prevAttachments, ...fileList];
  });
};